import * as XLSX from "xlsx";
import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, Trash2, Pencil, Repeat } from "lucide-react";
import "./scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import BookingModal from "../../components/scheduling/BookingModal";
import Toast from "../../components/scheduling/Toast";
import { SkeletonRow } from "../../components/scheduling/Skeleton";

function fmt(dt) {
  const d = new Date(dt);
  return d.toLocaleString();
}

export default function BookingsList() {
  const { companyId } = useCompany();

  const [spaces, setSpaces] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const [filters, setFilters] = useState({
    space_id: "",
    status: "confirmed",
    from: "",
    to: "",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);

  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });

  const spaceMap = useMemo(() => {
    const m = new Map();
    spaces.forEach((s) => m.set(s.id, s));
    return m;
  }, [spaces]);

  async function loadAll() {
    if (!companyId) return;
    setBusy(true);
    setErr("");
    try {
      const { data: sData, error: sErr } = await supabase
        .from("spaces")
        .select("*")
        .eq("company_id", companyId)
        .order("name");

      if (sErr) throw sErr;
      setSpaces(sData || []);

      let q = supabase
        .from("space_bookings")
        .select("*")
        .eq("company_id", companyId)
        .order("start_at", { ascending: true });

      if (filters.space_id) q = q.eq("space_id", filters.space_id);
      if (filters.status) q = q.eq("status", filters.status);
      if (filters.from)
        q = q.gte("start_at", new Date(filters.from).toISOString());
      if (filters.to) q = q.lte("start_at", new Date(filters.to).toISOString());

      const { data: bData, error: bErr } = await q;
      if (bErr) throw bErr;
      setBookings(bData || []);
    } catch (e) {
      setErr(e?.message || "Failed to load bookings");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.space_id, filters.status, filters.from, filters.to]);

  useEffect(() => {
    if (!companyId) return;
    const channel = supabase
      .channel("sched_bookings")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "space_bookings",
          filter: `company_id=eq.${companyId}`,
        },
        () => loadAll()
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  function exportToExcel() {
    const rows = bookings.map((b) => {
      const sp = spaceMap.get(b.space_id);
      return {
        Space: sp?.name || "—",
        Title: b.private_mode ? "Busy" : b.title || "—",
        Start: new Date(b.start_at).toLocaleString(),
        End: new Date(b.end_at).toLocaleString(),
        Status: b.status,
        BookedBy: b.booked_by,
        Notes: b.notes || "",
        SeriesId: b.series_id || "",
        SeriesDate: b.series_occurrence_date || "",
        CreatedAt: new Date(b.created_at).toLocaleString(),
        UpdatedAt: new Date(b.updated_at).toLocaleString(),
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `kostpadi_bookings_${stamp}.xlsx`);
  }

  async function cancelBooking(b) {
    const reason = window.prompt(
      "Reason for cancellation? (required for audit)"
    );
    if (!reason || reason.trim().length < 3) return;

    try {
      const { error } = await supabase.rpc("rpc_cancel_space_booking", {
        p_company_id: companyId,
        p_booking_id: b.id,
        p_reason: reason.trim(),
      });
      if (error) throw error;
      setToast({ open: true, type: "success", message: "Booking cancelled" });
      loadAll();
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        message: e?.message || "Failed to cancel",
      });
    }
  }

  async function cancelSeries(seriesId) {
    const reason = window.prompt(
      "Reason for cancelling this recurring series? (required for audit)"
    );
    if (!reason || reason.trim().length < 3) return;

    try {
      const { data, error } = await supabase.rpc("rpc_cancel_booking_series", {
        p_company_id: companyId,
        p_series_id: seriesId,
        p_reason: reason.trim(),
      });
      if (error) throw error;

      setToast({
        open: true,
        type: "success",
        message: `Series cancelled (${
          data || 0
        } future occurrence(s) cancelled)`,
      });
      loadAll();
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        message: e?.message || "Failed to cancel series",
      });
    }
  }

  return (
    <div className="sched-card">
      <div className="sched-card-head">
        <div>
          <h3 className="sched-h3">Bookings</h3>
          <p className="sched-muted">
            Company-wide visibility. Private bookings show as “Busy”.
          </p>
        </div>

        <button
          className="sched-btn primary"
          onClick={() => {
            setEditBooking(null);
            setModalOpen(true);
          }}
        >
          <Plus size={16} /> New booking
        </button>
        <button
          className="sched-btn ghost"
          onClick={exportToExcel}
          disabled={busy || bookings.length === 0}
        >
          Export Excel
        </button>
      </div>

      <div className="sched-filters sticky">
        <div className="sched-filter">
          <label>Space</label>
          <select
            value={filters.space_id}
            onChange={(e) =>
              setFilters((p) => ({ ...p, space_id: e.target.value }))
            }
          >
            <option value="">All</option>
            {spaces.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="sched-filter">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters((p) => ({ ...p, status: e.target.value }))
            }
          >
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
            <option value="">All</option>
          </select>
        </div>

        <div className="sched-filter">
          <label>From</label>
          <input
            type="date"
            value={filters.from}
            onChange={(e) =>
              setFilters((p) => ({ ...p, from: e.target.value }))
            }
          />
        </div>

        <div className="sched-filter">
          <label>To</label>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
          />
        </div>

        <button
          className="sched-btn ghost"
          onClick={() =>
            setFilters({ space_id: "", status: "confirmed", from: "", to: "" })
          }
          title="Reset"
        >
          <X size={16} /> Reset
        </button>
      </div>

      {busy ? (
        <>
          <SkeletonRow cols={6} />
          <SkeletonRow cols={6} />
        </>
      ) : err ? (
        <div className="sched-error">{err}</div>
      ) : bookings.length === 0 ? (
        <div className="sched-empty">
          <div className="sched-empty-title">No bookings found</div>
          <div className="sched-muted">
            Try a different filter or create a booking.
          </div>
        </div>
      ) : (
        <div className="sched-table">
          <div className="sched-thead">
            <span>Space</span>
            <span>Title</span>
            <span>Start</span>
            <span>End</span>
            <span>Status</span>
            <span className="right">Actions</span>
          </div>

          {bookings.map((b) => {
            const sp = spaceMap.get(b.space_id);
            const title = b.private_mode ? "Busy" : b.title || "—";
            const isSeries = !!b.series_id;

            return (
              <div key={b.id} className="sched-tr">
                <span>{sp?.name || "—"}</span>
                <span className="ellipsis">
                  {title}
                  {isSeries ? (
                    <span
                      className="sched-pill"
                      style={{
                        marginLeft: 8,
                        borderColor: "rgba(0,128,128,0.35)",
                        background: "rgba(0,128,128,0.06)",
                        color: "#008080",
                      }}
                      title="Recurring booking instance"
                    >
                      recurring
                    </span>
                  ) : null}
                </span>
                <span>{fmt(b.start_at)}</span>
                <span>{fmt(b.end_at)}</span>
                <span>
                  <span
                    className={`sched-pill ${
                      b.status === "confirmed"
                        ? "ok"
                        : b.status === "cancelled"
                        ? "off"
                        : ""
                    }`}
                  >
                    {b.status}
                  </span>
                </span>
                <span className="right">
                  <button
                    className="sched-mini"
                    onClick={() => {
                      setEditBooking(b);
                      setModalOpen(true);
                    }}
                    disabled={b.status !== "confirmed"}
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>

                  {b.series_id ? (
                    <button
                      className="sched-mini"
                      onClick={() => cancelSeries(b.series_id)}
                      disabled={b.status !== "confirmed"}
                      title="Cancel recurring series (future occurrences only)"
                    >
                      <Repeat size={16} />
                    </button>
                  ) : null}

                  <button
                    className="sched-mini danger"
                    onClick={() => cancelBooking(b)}
                    disabled={b.status !== "confirmed"}
                    title="Cancel"
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}

      <BookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={editBooking ? "edit" : "create"}
        booking={editBooking}
        spaces={spaces.filter((s) => s.status === "active")}
        onSaved={() => loadAll()}
      />

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </div>
  );
}
